import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { LinkedProductTypes } from '@atlassian/cssxp-ccp-types';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { getParentContainerId, currentEntitlementType, getChargeElement } from '@css-xp/base/entitlement-utils';
import { LinkedEntitlementTypes, EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import {
  FetchNativeProductCollectionForChangeSubscriptionQuery,
  FetchNativeProductCollectionForChangeSubscriptionQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-sxp';
import { isCollectionProductEntitlement } from '@css-xp/ppc-utils';
import { useFetchLinkedEntitlementsPPCQuery } from '../../../../hooks/use-fetch-linked-entitlements-ppc-query';
import { fetchLinkedEntitlementChangeSubscription } from '../../../../queries/fetch-linked-entitlement-change-subscription';
import { fetchNativeProductCollectionForChangeSubscription } from '../../../../queries/fetch-native-product-collection-for-change-subscription';

export const useChangeSubscriptionForCollectionProduct = ({ entitlement }: { entitlement: EntitlementDetails }) => {
  const {
    loading: linkedSubscriptionsLoading,
    data: linkedSubscriptionsData,
    error: linkedSubscriptionsError,
  } = useFetchLinkedEntitlementsPPCQuery({
    entitlementId: entitlement.id,
    containerId: getParentContainerId(entitlement),
    currentEntitlementType: currentEntitlementType(entitlement),
    destinationEntitlementType: [LinkedEntitlementTypes.COLLECTION_INSTANCE],
    productType: entitlement.productType as LinkedProductTypes,
    findProducts: true,
    findApps: false,
    skipOption: !isCollectionProductEntitlement(entitlement?.productId),
    transactionAccountId: entitlement.transactionAccountId,
    query: fetchLinkedEntitlementChangeSubscription,
  });

  const linkedSubscriptions = ((linkedSubscriptionsData?.entitlementDetails?.linkedEntitlements ??
    []) as unknown) as Array<{
    id: string;
    entitlementNumber: string;
    productId: string;
    productName: string;
    offeringId: string;
  }>;

  const client = useMemo(() => abgGraphqlClient(ProxyGraphClients.SXP), []);

  const {
    data: nativeProductCollectionData,
    error: nativeProductCollectionError,
    loading: nativeProductCollectionLoading,
  } = useQuery<
    FetchNativeProductCollectionForChangeSubscriptionQuery,
    FetchNativeProductCollectionForChangeSubscriptionQueryVariables
  >(fetchNativeProductCollectionForChangeSubscription, {
    client,
    variables: {
      productKey: entitlement?.productId,
    },
    nextFetchPolicy: 'cache-first',
    skip: !isCollectionProductEntitlement(entitlement?.productId),
  });

  const getPayloadForLinkedCollectionProduct = ({
    requestOfferingId,
    requestChargeQuantity,
    requestSkipTrial,
  }: {
    requestOfferingId: string | undefined;
    requestChargeQuantity: number;
    requestSkipTrial: boolean;
  }) => {
    const offeringCollections = nativeProductCollectionData?.nativeCatalogForCollection?.nodes[0]?.offeringCollections;
    const productOfferingsForCollection =
      nativeProductCollectionData?.nativeCatalogForCollection?.nodes[0]?.productOfferingsForCollection;

    const instancesOfferingIdsBasedOnParentOffering = offeringCollections?.find(
      (offering) => requestOfferingId && offering?.id === requestOfferingId
    )?.toOfferingIds;

    const entitlementRequest = linkedSubscriptions.map((linkedSubscription) => {
      const currentProductOfferings = productOfferingsForCollection?.filter(
        (productOffering) => productOffering?.product?.id === linkedSubscription?.productId
      );
      const destinationOffering = currentProductOfferings?.find(
        (productOffering) =>
          productOffering?.id && instancesOfferingIdsBasedOnParentOffering?.includes(productOffering?.id)
      );
      return {
        entitlementId: linkedSubscription.id,
        orderType: OrderItemType.AMENDMENT_ORDER,
        productId: linkedSubscription.productId,
        offeringId: destinationOffering?.id,
        chargeQuantity: requestChargeQuantity,
        chargeElement: getChargeElement(linkedSubscription.productId as string),
        upgradeCollectionRelationship: true,
        skipTrial: requestSkipTrial,
      };
    });

    return entitlementRequest;
  };

  return {
    linkedSubscriptions,
    collectionDataLoading: linkedSubscriptionsLoading || nativeProductCollectionLoading,
    collectionDataError: linkedSubscriptionsError || nativeProductCollectionError,
    getPayloadForLinkedCollectionProduct,
  };
};
